import { IntelligemsAppScopeType } from "@intelligems/ig-types";

/**
 * What approving each Shopify scope unlocks, phrased to slot into
 * "To access <advantages>, confirm new permissions in Shopify."
 *
 * Scopes with no entry fall back to {@link DEFAULT_SHOPIFY_SCOPE_ADVANTAGE},
 * so adding a scope to the app's canonical list never breaks this copy — add
 * an entry here only when the scope is worth naming on its own. Scopes that
 * unlock the same thing may share a phrase; it is only pitched once.
 */
export const SHOPIFY_SCOPE_ADVANTAGES: Partial<
  Record<IntelligemsAppScopeType, string>
> = {
  read_analytics: "the latest analytics features",
  read_reports: "the latest analytics features",
  read_all_orders: "historical order analytics",
  unauthenticated_read_product_listings: "storefront product data",
  unauthenticated_read_product_tags: "storefront product data",
  unauthenticated_read_selling_plans: "storefront product data",
  unauthenticated_read_product_pickup_locations: "storefront product data",
  unauthenticated_read_product_inventory: "inventory-aware recommendations",
  unauthenticated_read_content: "storefront content",
  unauthenticated_read_metaobjects: "storefront content",
  unauthenticated_read_customers: "storefront customer personalization",
  unauthenticated_write_customers: "storefront customer personalization",
  unauthenticated_read_customer_tags: "storefront customer personalization",
  unauthenticated_read_checkouts: "storefront checkout experiences",
  unauthenticated_write_checkouts: "storefront checkout experiences",
  read_files: "image and file assets",
  write_files: "image and file assets",
  read_cart_transforms: "cart bundling",
  write_cart_transforms: "cart bundling",
  read_discounts: "discount-based offers",
  write_discounts: "discount-based offers",
  read_shipping: "shipping tests",
  write_shipping: "shipping tests",
  read_pixels: "checkout conversion tracking",
  write_pixels: "checkout conversion tracking",
  read_customer_events: "on-site event tracking",
  read_customers: "customer targeting",
  read_content: "page and content targeting",
  read_locations: "location-level inventory",
  read_checkout_branding_settings: "checkout branding tests",
  read_checkout_and_accounts_configurations: "checkout and accounts tests",
  write_orders: "post-purchase offers",
};

export const DEFAULT_SHOPIFY_SCOPE_ADVANTAGE = "the latest features";

/**
 * How many advantages the banner names before it stops enumerating. Past this
 * the copy reads as a list dump, so a request spanning many capabilities —
 * which is what adding a batch of scopes produces — gets the generic line
 * instead.
 */
export const MAX_SHOPIFY_SCOPE_ADVANTAGES = 3;

/**
 * The advantages of approving `missingScopes`, in the order given (the
 * canonical scope order) and de-duplicated. Falls back to the generic
 * advantage when none of the missing scopes has copy of its own, or when the
 * missing scopes span more capabilities than the banner will name.
 */
export const getShopifyScopeAdvantages = (
  missingScopes: readonly IntelligemsAppScopeType[]
): string[] => {
  const advantages = new Set<string>();

  for (const scope of missingScopes) {
    const advantage = SHOPIFY_SCOPE_ADVANTAGES[scope];
    if (advantage) {
      advantages.add(advantage);
    }
  }

  if (advantages.size === 0 || advantages.size > MAX_SHOPIFY_SCOPE_ADVANTAGES) {
    return [DEFAULT_SHOPIFY_SCOPE_ADVANTAGE];
  }

  return [...advantages];
};
